import { useState } from 'react';
import { webAppConfig, type WebAppConfig } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, Copy } from 'lucide-react';

export default function WebApp() {
  const [config, setConfig] = useState<WebAppConfig>(webAppConfig.get());

  const handleChange = (key: keyof WebAppConfig, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    webAppConfig.set(config);
    toast.success('Web App configuration saved');
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-2xl font-bold text-white">Google Web App</h1>
        <p className="text-white/60 text-sm mt-1">Configure your Google Apps Script Web App for form submissions.</p>
      </div>

      <div className="card-dark p-6 space-y-5">
        {[
          { key: 'deploymentId' as keyof WebAppConfig, label: 'Deployment ID', placeholder: 'AKfycb...', helper: 'The deployment ID from your GAS project' },
          { key: 'webAppUrl' as keyof WebAppConfig, label: 'Web App URL', placeholder: 'https://script.google.com/macros/s/.../exec', helper: 'The full published Web App URL' },
          { key: 'libraryUrl' as keyof WebAppConfig, label: 'Library URL', placeholder: 'https://script.google.com/...', helper: 'Script library URL if using one' },
          { key: 'scriptId' as keyof WebAppConfig, label: 'Script ID', placeholder: '1x...', helper: 'The Google Apps Script project ID' },
        ].map(({ key, label, placeholder, helper }) => (
          <div key={key} className="space-y-2">
            <Label className="text-white/80 text-sm">{label}</Label>
            <Input
              value={config[key]}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder={placeholder}
              className="h-11 bg-[#0057B7] border-white/10 text-white"
            />
            <p className="text-xs text-white/60">{helper}</p>
          </div>
        ))}

        <Button onClick={handleSave} className="btn-primary">
          <Save className="w-4 h-4" />
          Save Web App Config
        </Button>
      </div>

      {/* Quick Reference */}
      <div className="card-dark p-6">
        <h3 className="font-heading font-semibold text-white mb-4">Quick Reference</h3>
        <div className="space-y-3">
          {[
            { label: 'Deployment ID', value: config.deploymentId || 'Not set' },
            { label: 'Web App URL', value: config.webAppUrl || 'Not set' },
            { label: 'Library URL', value: config.libraryUrl || 'Not set' },
            { label: 'Script ID', value: config.scriptId || 'Not set' },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <span className="text-sm text-white/60">{label}</span>
              <div className="flex items-center gap-2">
                <code className="text-xs text-[#0057B7] bg-[#FFDD00]/10 px-2 py-1 rounded truncate max-w-[300px]">
                  {value}
                </code>
                {value !== 'Not set' && (
                  <button onClick={() => handleCopy(value)} className="text-white/60 hover:text-white/60">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* GAS Template */}
      <div className="card-dark p-6">
        <h3 className="font-heading font-semibold text-white mb-3">Apps Script Template</h3>
        <pre className="bg-[#0057B7] p-4 rounded-lg text-xs text-white/70 overflow-auto max-h-64">
{`function doPost(e) {
  var sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  var data = e.parameter;
  
  sheet.appendRow([
    new Date(),
    data.first_name,
    data.last_name,
    data.email,
    data.phone,
    data.brand,
    data.model,
    data.symptoms,
    data.service_type || 'collection'
  ]);
  
  // Send confirmation email
  MailApp.sendEmail({
    to: data.email,
    subject: 'Repair Request Received - ' + data.brand + ' ' + data.model,
    body: 'Thank you for your repair request. We will contact you within 24 hours.'
  });
  
  return ContentService.createTextOutput(
    JSON.stringify({ status: 'success', message: 'Request received' })
  ).setMimeType(ContentService.MimeType.JSON);
}

function doGet(e) {
  return ContentService.createTextOutput(
    JSON.stringify({ status: 'OK' })
  ).setMimeType(ContentService.MimeType.JSON);
}`}
        </pre>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleCopy(document.querySelector('pre')?.textContent || '')}
          className="mt-3 text-xs border-white/10 text-white/60 hover:text-white hover:bg-white/5"
        >
          <Copy className="w-3 h-3 mr-1" />
          Copy Template
        </Button>
      </div>
    </div>
  );
}